import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faBell, faEnvelope } from '@/fontawesome.ts';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import CardCheckbox from '@/components/ui/CardCheckbox';
import type { RecommendationCreateStats } from '@/models';

interface NotificationStatsCardCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  colorTheme?: 'blue' | 'green';
  estimatedStats: RecommendationCreateStats | null;
  statsLoading: boolean;
  statsError: string | null;
  totalUserCount: number | null;
}

export default function NotificationStatsCardCheckbox({
  checked,
  onChange,
  disabled,
  colorTheme,
  estimatedStats,
  statsLoading,
  statsError,
  totalUserCount
}: NotificationStatsCardCheckboxProps) {
  return (
    <CardCheckbox
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      colorTheme={colorTheme}
    >
      <div className="flex items-center justify-between">
        <div>
          <FontAwesomeIcon icon={faBell} className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Benachrichtigungen senden</span>
        </div>
        {statsLoading && (
          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>

      {statsError !== null && (
        <p className="text-xs text-red-500 dark:text-red-400 mt-2">{statsError}</p>
      )}

      <div className="mt-2 space-y-1">
        {estimatedStats !== null ? (
          <>
            <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.discordMessages === 0 ? 'opacity-50' : ''}`}>
              <FontAwesomeIcon icon={faDiscord} className="w-3 h-3 mr-2" />
              Discord-Nachrichten: {estimatedStats.discordMessages}
            </p>
            <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.telegramMessages === 0 ? 'opacity-50' : ''}`}>
              <FontAwesomeIcon icon={faTelegram} className="w-3 h-3 mr-2" />
              Telegram-Nachrichten: {estimatedStats.telegramMessages}
            </p>
            <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.scnNotifications === 0 ? 'opacity-50' : ''}`}>
              <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
              SCN Push-Nachrichten: {estimatedStats.scnNotifications}
            </p>
            <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.pushNotifications === 0 ? 'opacity-50' : ''}`}>
              <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
              Push (App): {estimatedStats.pushNotifications}
            </p>
            <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.mailNotifications === 0 ? 'opacity-50' : ''}`}>
              <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 mr-2" />
              E-Mails an {estimatedStats.mailNotifications}{totalUserCount !== null ? ` von insgesamt ${totalUserCount} Nutzern` : ''}
            </p>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <FontAwesomeIcon icon={faDiscord} className="w-3 h-3 mr-2" />
              Sendet Discord-Nachrichten in die entsprechenden Channel
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <FontAwesomeIcon icon={faTelegram} className="w-3 h-3 mr-2" />
              Sendet Telegram-Nachrichten
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
              Sendet SCN Push-Benachrichtigungen an Nutzer, welche das konfiguriert haben
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
              Sendet Push-Benachrichtigungen (App) an Nutzer, welche diese aktiviert haben
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 mr-2" />
              Sendet E-Mails an Nutzer
            </p>
          </>
        )}
      </div>
    </CardCheckbox>
  );
}
